import type {
  CreateRiskControlRequest,
  CreateRiskControlResponse,
  DeleteRiskControlRequest,
  ListRiskControlsArgs,
  RiskControlResponse,
  RiskControlVolumesResponse,
  UpdateRiskControlRequest,
  UpdateRiskControlResponse,
} from '@moment/trading-types/risk'
import type {
  CreateEditChecksRequest,
  CreateEditChecksResponse,
  GetEditChecksResponse,
} from '@/trading/types/api/edit-checks'
import type { AggregatedOrderView } from '@/types/api/aggregated/order'
import type {
  AttestationResponse,
  AttestationSpec,
  EvaluateAttestationsRequest,
  EvaluateAttestationsResponse,
  UpdateAttestationArgs,
} from '@/types/api/attestations'
import type {
  BulkCancelOrdersRequest,
  BulkCancelOrdersResponse,
} from '@/types/api/bulk-order-cancel'
// TODO: Migrate to types from @/legacy-api/orders-slice.gen (BulkOrderSubmissionRequest, BulkOrderSubmissionResponse)
import type {
  BulkOrderSubmissionRequest,
  BulkOrderSubmissionResponse,
} from '@/types/api/bulk-order-submission'
import type {
  TradingEventMessage,
  TradingEventsArgs,
} from '@/types/api/events/trading-event'
import type {
  CancelOrderExecutionRequest,
  CancelOrderExecutionResponse,
  OEMSPlaceOrderRequest,
  OEMSPlaceOrderResponse,
} from '@/types/api/order'
import type {
  HydrateQuotesRequest,
  HydrateQuotesResponse,
} from '@/types/api/quotes'
import type { TradingCalendar } from '@/types/api/trading-calendar'
import type {
  UnifiedMarketArgs,
  UnifiedMarketResponse,
} from '@/types/api/unified-markets'
import type {
  VenueConfiguration,
  VenueConnection,
} from '@/types/api/venue-configurations'

import { RiskControlType } from '@moment/trading-types/risk'

import { riskControlsQuery } from '@/legacy-api/helpers/risk-controls-query'
import { setAnnotation } from '@/trading/order-entry/helpers/set-annotation'
import { TagTypes } from '@/types/api/tag-types'
import { getTodayStartHwm } from '@/utils/dates/dateHelpers'
import { getFilterStartAndEndTimes } from '@/utils/trade-blotter/helpers'
import { filterAndProcessTradingEvents } from '@/utils/trade-blotter/trading-events/helpers'

import { baseApiSlice } from './base-api-slice'

export const RISK_POLLING_INTERVAL = 1000

/** The attestations page's list args, exported so the page's toggle hook and
 *  this query name one cache entry. */
export const ATTESTATION_CONTROL_ARGS: ListRiskControlsArgs = {
  types: [RiskControlType.Attestation],
}

const EVENTS_LIMIT = 10000

// Safety cap: 100 * 10000 = 1M events, well beyond any realistic 30-day window.
// Prevents a runaway loop if the server ever returns has_more=true indefinitely.
const TRADING_EVENTS_MAX_PAGES = 100

const tradingSlice = baseApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    placeOrder: builder.mutation<OEMSPlaceOrderResponse, OEMSPlaceOrderRequest>(
      {
        query: (body) => ({
          url: `trading/order/`,
          method: 'POST',
          body,
        }),
        invalidatesTags: [TagTypes.Order],
      },
    ),
    getUnifiedMarket: builder.query<UnifiedMarketResponse, UnifiedMarketArgs>({
      query: ({ id, riskGroupId, skipFees, settlementDate }) => ({
        url: `trading/market/${id}/`,
        params: {
          risk_group_id: riskGroupId,
          ...(skipFees !== undefined && { skip_fees: skipFees }),
          ...(settlementDate !== undefined && {
            settlement_date: settlementDate,
          }),
        },
        method: 'GET',
      }),
      providesTags: [TagTypes.UnifiedMarket],
    }),
    getTradingEventsForOrder: builder.query<
      { data: TradingEventMessage[] | null },
      string
    >({
      query: (orderId) => ({
        url: `/trading/events/`,
        method: 'GET',
        params: { order_id: orderId },
      }),
    }),
    getTradingEvents: builder.query<AggregatedOrderView[], TradingEventsArgs>({
      async queryFn({ startHwm, endHwm }, _api, _extraOptions, fetchWithBQ) {
        const events: TradingEventMessage[] = []
        let nextStart: string | undefined = startHwm || getTodayStartHwm()
        let pages = 0

        while (nextStart !== undefined && pages < TRADING_EVENTS_MAX_PAGES) {
          pages += 1
          const result = await fetchWithBQ({
            url: 'trading/events/',
            method: 'GET',
            params: {
              start: nextStart,
              ...(endHwm && { end: endHwm }),
              limit: EVENTS_LIMIT,
            },
          })
          if (result.error) return { error: result.error }
          // has_more is optional in the cast so an older backend that omits the
          // field falls through to a single-page fetch — which is correct against
          // pre-pagination backends that return everything in one response.
          const page = result.data as {
            data: TradingEventMessage[]
            has_more?: boolean
          }
          events.push(...page.data)

          const lastMsgNumber = page.data.at(-1)?.msg_number
          // Continue only on an explicit has_more === true with a usable cursor.
          // Anything else (false, undefined, empty page) stops the loop.
          nextStart =
            page.has_more === true && lastMsgNumber !== undefined
              ? (BigInt(lastMsgNumber) + 1n).toString()
              : undefined
        }

        // For the historical orders page, use the start date instead of today and supply an end date
        // For the trade blotter, use today as the start date and no end date
        const { filterStartTime, filterEndTime } = getFilterStartAndEndTimes(
          startHwm,
          endHwm,
        )
        return {
          data: filterAndProcessTradingEvents(
            events,
            filterStartTime,
            filterEndTime,
          ),
        }
      },
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, { cacheKey }) => [
        {
          type: TagTypes.TradingEvents,
          id: cacheKey,
        },
      ],
    }),
    hydrateQuotes: builder.query<HydrateQuotesResponse, HydrateQuotesRequest>({
      query: (body) => ({
        url: 'trading/quotes/hydrate/',
        method: 'POST',
        body,
      }),
    }),

    //
    // Trading Risk Controls
    //
    // https://docs.withmoment.com/reference/get_v1-trading-risk-controls
    listRiskControls: builder.query<
      RiskControlResponse[],
      ListRiskControlsArgs | undefined
    >({
      query: (args) => ({
        url: `trading/risk/controls/${riskControlsQuery(args)}`,
        method: 'GET',
      }),
      providesTags: [TagTypes.RiskControl],
    }),
    // https://docs.withmoment.com/reference/post_v1-trading-risk-controls
    createRiskControl: builder.mutation<
      CreateRiskControlResponse,
      CreateRiskControlRequest
    >({
      query: (control) => ({
        url: 'trading/risk/controls/',
        method: 'POST',
        body: control,
      }),
      invalidatesTags: [TagTypes.RiskControl],
    }),
    // https://docs.withmoment.com/reference/delete_v1-trading-risk-controls-id
    deleteRiskControl: builder.mutation<void, DeleteRiskControlRequest>({
      query: ({ control_id }) => ({
        url: `trading/risk/controls/${control_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.RiskControl],
    }),
    // https://docs.withmoment.com/reference/get_v1-trading-risk-controls-id
    getRiskControl: builder.query<RiskControlResponse, string>({
      query: (controlId) => ({
        url: `trading/risk/controls/${controlId}/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.RiskControl],
    }),
    getRiskControlVolumes: builder.query<RiskControlVolumesResponse, string>({
      query: (controlId) => ({
        url: `trading/risk/controls/${controlId}/volumes/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.RiskControl],
    }),

    // https://docs.withmoment.com/reference/put_v1-trading-risk-controls-id
    updateRiskControl: builder.mutation<
      UpdateRiskControlResponse,
      UpdateRiskControlRequest
    >({
      query: ({ id, control }) => ({
        url: `trading/risk/controls/${id}/`,
        method: 'PUT',
        body: control,
      }),
      invalidatesTags: [TagTypes.RiskControl],
    }),

    //
    // Trading Attestations
    //
    // Parent-org surface: child orgs receive attestations at order entry, not here.
    listAttestations: builder.query<AttestationResponse[], void>({
      query: () => ({
        url: 'trading/risk/attestations/',
        method: 'GET',
      }),
      providesTags: [TagTypes.Attestation],
    }),
    createAttestation: builder.mutation<AttestationResponse, AttestationSpec>({
      query: (spec) => ({
        url: 'trading/risk/attestations/',
        method: 'POST',
        body: spec,
      }),
      invalidatesTags: [TagTypes.Attestation],
    }),
    // Appends a version rather than rewriting the attestation: orders that
    // already resolved an earlier one keep reading what the advisor attested to.
    updateAttestation: builder.mutation<
      AttestationResponse,
      UpdateAttestationArgs
    >({
      query: ({ attestationId, spec }) => ({
        url: `trading/risk/attestations/${attestationId}/`,
        method: 'PUT',
        body: spec,
      }),
      invalidatesTags: [TagTypes.Attestation],
    }),
    // Archives rather than erases: the attestation leaves the list, and the
    // versions an order already resolved stay readable.
    deleteAttestation: builder.mutation<void, string>({
      query: (attestationId) => ({
        url: `trading/risk/attestations/${attestationId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.Attestation],
    }),
    /**
     * Which attestations an order requires, per account leg — the order-entry
     * counterpart to the authoring endpoints above. A POST because the order is
     * the query, not a resource: nothing is written.
     *
     * Advisory. Submit-time validation is authoritative, and the two agree
     * because validation-engine answers both off one evaluation path.
     *
     * Not cached: the response carries presigned document urls and a MuniPOINTS
     * session url, and comes back `Cache-Control: no-store`.
     */
    evaluateAttestations: builder.query<
      EvaluateAttestationsResponse,
      EvaluateAttestationsRequest
    >({
      query: (order) => ({
        url: 'trading/risk/attestations/evaluate/',
        method: 'POST',
        body: order,
      }),
      keepUnusedDataFor: 0,
    }),

    //
    // Manual Approvals
    //
    // ID returned is the ID of the manual approval request,
    // while ID supplied is the ID of the order to approve
    manuallyApproveOrder: builder.mutation<
      { id: string },
      { orderId: string; annotation?: string }
    >({
      query: ({ orderId, annotation }) => ({
        url: `trading/order/${orderId}/approve/`,
        method: 'POST',
        body: {
          annotation: setAnnotation(annotation),
        },
      }),
    }),
    // ID returned is the ID of the manual rejection request,
    // while ID supplied is the ID of the order to reject
    manuallyRejectOrder: builder.mutation<
      { id: string },
      { orderId: string; annotation?: string; reason?: string }
    >({
      query: ({ orderId, annotation, reason }) => ({
        url: `trading/order/${orderId}/reject/`,
        method: 'POST',
        body: {
          annotation: setAnnotation(annotation),
          reason: reason ?? '',
        },
      }),
    }),
    cancelOrderExecution: builder.mutation<
      CancelOrderExecutionResponse,
      CancelOrderExecutionRequest
    >({
      query: ({ order_id, execution_order_id }) => ({
        url: `trading/order/${order_id}/execution-order/${execution_order_id}/`,
        method: 'DELETE',
      }),
    }),
    bulkOrderSubmissionLegacy: builder.mutation<
      BulkOrderSubmissionResponse,
      BulkOrderSubmissionRequest
    >({
      query: (body) => ({
        url: 'trading/order/bulk/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.Order],
      // TODO: TR-830 - Remove this duplicate endpoint and switch to orders-slice.gen
      // Currently used due to name collision with orders-slice.gen.ts
      // Eventually we want to switch over to the generated endpoint, and remove this endpoint.
      // This is a temporary solution to avoid breaking changes.
    }),
    cancelOrder: builder.mutation<
      void,
      { id: string; cancelGroupMembers?: boolean }
    >({
      // cancelGroupMembers also cancels the open quantity of every member of a
      // group parent order. Only valid for GPOs; the backend rejects it on any
      // other order, so callers pass true only for group-order cancels. Send
      // the param only when true so non-group cancels stay byte-identical to a
      // plain cancel.
      query: ({ id, cancelGroupMembers }) => {
        const params = cancelGroupMembers
          ? { cancel_group_members: true }
          : undefined
        return {
          url: `trading/order/${id}/`,
          method: 'DELETE',
          params,
        }
      },
      invalidatesTags: [TagTypes.Order, TagTypes.TradingEvents],
    }),
    // Requests cancellation of an order's allocation. The backend emits a
    // cancel event and returns 204; the outcome (canceled / cancel-rejected)
    // arrives asynchronously via the real-time orders stream.
    cancelOrderAllocation: builder.mutation<void, string>({
      query: (allocationId) => ({
        url: `trading/allocation/${allocationId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.Order, TagTypes.TradingEvents],
    }),
    bulkCancelOrders: builder.mutation<
      BulkCancelOrdersResponse,
      BulkCancelOrdersRequest
    >({
      query: (body) => ({
        url: 'trading/order/bulk-cancel/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.Order, TagTypes.TradingEvents],
    }),
    getVenueConfigurations: builder.query<VenueConfiguration[], void>({
      query: () => ({
        url: 'trading/venue-configurations/',
        method: 'GET',
      }),
    }),
    getTradingCalendar: builder.query<
      TradingCalendar,
      {
        startDate: string
        endDate: string
      }
    >({
      query: ({ startDate, endDate }) => ({
        url: 'trading/calendar/',
        method: 'GET',
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      }),
    }),
    getEditChecks: builder.query<GetEditChecksResponse, string>({
      query: (id) => ({
        url: `trading/validation/edit-check/${id}/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.EditChecks],
    }),
    createEditChecks: builder.mutation<
      CreateEditChecksResponse,
      CreateEditChecksRequest
    >({
      query: (body) => ({
        url: 'trading/validation/edit-check/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.EditChecks],
    }),
    getTradingVenues: builder.query<VenueConnection[], void>({
      query: () => ({
        url: 'trading/venue/',
        method: 'GET',
      }),
      transformResponse: (response: { data: VenueConnection[] }) =>
        response.data,
    }),
  }),
})

export const {
  endpoints: {
    placeOrder,
    getUnifiedMarket,
    getTradingEvents,
    getTradingEventsForOrder,
    listRiskControls,
    createRiskControl,
    getRiskControl,
    getRiskControlVolumes,
    updateRiskControl,
    deleteRiskControl,
    listAttestations,
    createAttestation,
    evaluateAttestations,
    manuallyApproveOrder,
    manuallyRejectOrder,
    cancelOrderExecution,
    bulkOrderSubmissionLegacy,
    cancelOrder,
    cancelOrderAllocation,
    bulkCancelOrders,
    getVenueConfigurations,
    getTradingCalendar,
    getEditChecks,
    createEditChecks,
    hydrateQuotes,
    getTradingVenues,
  },

  useGetUnifiedMarketQuery,
  useGetTradingEventsQuery,
  useGetTradingEventsForOrderQuery,
  useListRiskControlsQuery,
  useCreateRiskControlMutation,
  useGetRiskControlQuery,

  useUpdateRiskControlMutation,
  useDeleteRiskControlMutation,

  useListAttestationsQuery,
  useCreateAttestationMutation,
  useUpdateAttestationMutation,
  useDeleteAttestationMutation,
  useEvaluateAttestationsQuery,

  useCancelOrderMutation,
  useCancelOrderAllocationMutation,
  useBulkCancelOrdersMutation,

  useGetTradingCalendarQuery,
  useGetEditChecksQuery,

  useGetTradingVenuesQuery,
  useLazyGetUnifiedMarketQuery,
} = tradingSlice
